import { useQuery } from '@tanstack/react-query';

import buyerModule from '../../api/modules/buyer.module';
import { parseRand } from '../Dashboard/dashboard.parts';

/**
 * The buyer's collection summary: how many cards they hold and what those cards
 * are worth at market.
 *
 * WHY THIS IS A SEPARATE REQUEST FROM THE COLLECTION TABLE
 * -------------------------------------------------------
 * The table is paginated ten at a time. Summing `market_price` over the rows it
 * happens to be showing would produce a "portfolio value" that silently counts
 * ten cards out of two hundred, and a wrong number about somebody's money is
 * worse than no number at all. So the summary asks for the whole collection in
 * one request and sums that.
 *
 * `getSavedCards({ page, limit })` maps to the endpoint's `index` / `offset`.
 * Asking for a large page is an established call in this app: `CardDetail`
 * already fetches favourites with `offset: 100` to test membership.
 *
 * WHY IT CAN REFUSE TO ANSWER
 * ---------------------------
 * If the collection is larger than one request returns, the sum is a floor and
 * not a total, so `value` comes back `null` and the tile shows nothing rather
 * than a confident undercount. `complete` says which happened. There is no
 * server-side total to ask for; adding one is the only way to make this exact
 * for very large collections, and that is backend work.
 *
 * No new query key shape and no new endpoint: same `getSavedCards`, same
 * params, a different page size and its own cache key.
 */
const PAGE_SIZE = 200;

export function useCollectionSummary({ enabled = true } = {}) {
  const { data, isFetching, isError } = useQuery({
    queryKey: ['saved-cards-summary', { limit: PAGE_SIZE }],
    enabled,
    queryFn: async () => {
      const { success, data: response } = await buyerModule.getSavedCards({
        page: 1,
        limit: PAGE_SIZE,
      });
      if (!success) return null;
      return response || null;
    },
    // The collection changes when the buyer saves or unsaves, which is rare
    // compared to how often they open the dashboard.
    staleTime: 60_000,
  });

  const rows = data?.favourites || [];
  // `total` is the server's own count when it sends one; the row count is the
  // fallback and is only right when the collection fit in one page.
  const count = data?.total ?? (data ? rows.length : null);
  const complete = data ? rows.length >= (data.total ?? rows.length) : false;

  const value = complete
    ? rows.reduce((sum, row) => sum + (parseRand(row.market_price) || 0), 0)
    : null;

  return {
    count,
    value,
    complete,
    loading: isFetching,
    error: isError,
  };
}

export default useCollectionSummary;
